/**
 * Pure adapter functions translating `NavigationSettings` (the admin-
 * configured header/footer data, SF-NAV-101) plus the live category tree
 * into the prop shapes the `NavBar` / `Footer` capsules render.
 *
 * Kept out of `useStorefrontNavigation.ts` so the filtering/mapping rules
 * are unit-testable without mounting React or wiring Redux.
 */
import type { FooterBrand, FooterColumn, FooterContact } from '@dfq/shared/components/Footer';
import type { NavBarNavLink, NavBarUser } from '@dfq/shared/components/NavBar';
import type { SearchCategoryNode } from '@dfq/shared/stores/search';
import type { NavigationFooterColumn, NavigationLink, NavigationSettings } from '@dfq/shared/types/settings';

export interface StorefrontChrome {
  navItems: NavBarNavLink[];
  footerColumns: FooterColumn[];
  footerBrand: FooterBrand;
  footerContact: FooterContact;
  /** Full copyright line with `{year}` already resolved, or `undefined` when unset. */
  footerCopyrightText: string | undefined;
  /** SF-BRAND-101 — admin-configured header logo image URL, or `undefined` to fall back to the NavBar text wordmark. */
  headerLogoUrl: string | undefined;
}

/**
 * A link's `to` is safe to render as a `react-router-dom` destination only
 * when it's a site-relative path or an absolute http(s) URL. The platform
 * already rejects anything else on write (`javascript:`, `//host`,
 * malformed schemes) — this is defense in depth against a settings row
 * written before that validation existed, or a fixture with bad data.
 */
export function isValidLinkDestination(to: string): boolean {
  if (to.startsWith('/') && !to.startsWith('//')) return true;
  return /^https?:\/\//i.test(to);
}

/**
 * Flattens the visible category tree into a set of slugs. A hidden node's
 * subtree is excluded entirely — a link gated on a category nested under a
 * hidden ancestor is not "in the visible category tree" even if the node
 * itself carries `isVisible: true`.
 */
export function visibleCategorySlugs(tree: SearchCategoryNode[]): Set<string> {
  const slugs = new Set<string>();
  const visit = (nodes: SearchCategoryNode[]) => {
    for (const node of nodes) {
      if (!node.isVisible) continue;
      slugs.add(node.slug);
      visit(node.children);
    }
  };
  visit(tree);
  return slugs;
}

/**
 * Whether a settings-driven link should render: its destination must be a
 * safe path/URL, and its `requiresCategory` (when set) must name a slug
 * present in the visible category tree.
 */
function isLinkVisible(link: NavigationLink, categorySlugs: Set<string>): boolean {
  if (!isValidLinkDestination(link.to)) return false;
  if (link.requiresCategory === null) return true;
  return categorySlugs.has(link.requiresCategory);
}

function toNavBarLink(link: NavigationLink): NavBarNavLink {
  return { label: link.label, to: link.to };
}

/** Filters + maps the admin-configured header links to `NavBar`'s flat link shape. */
export function buildHeaderNavItems(
  headerLinks: readonly NavigationLink[],
  categorySlugs: Set<string>,
): NavBarNavLink[] {
  return headerLinks.filter((link) => isLinkVisible(link, categorySlugs)).map(toNavBarLink);
}

/**
 * Filters + maps the admin-configured footer columns. A column whose links
 * all get filtered out (missing category, unsafe `to`) is dropped entirely
 * rather than rendering an empty heading.
 */
export function buildFooterColumns(
  columns: readonly NavigationFooterColumn[],
  categorySlugs: Set<string>,
): FooterColumn[] {
  return columns
    .map((column) => ({
      title: column.title,
      links: column.links.filter((link) => isLinkVisible(link, categorySlugs)).map(toNavBarLink),
    }))
    .filter((column) => column.links.length > 0);
}

/** Replaces a `{year}` placeholder in the admin-configured copyright template. */
export function resolveFooterCopyright(
  template: string | null,
  year: number = new Date().getFullYear(),
): string | undefined {
  if (template === null) return undefined;
  return template.replace('{year}', String(year));
}

function orUndefined(value: string | null): string | undefined {
  return value ?? undefined;
}

/**
 * Builds the full storefront chrome bundle from the raw settings + category
 * tree. `settings` is `null` while the fetch is pending/failed — every
 * field degrades to an empty/undefined value rather than throwing, so the
 * shell renders (with just the utility cluster) before the fetch resolves.
 */
export function buildStorefrontChrome(
  settings: NavigationSettings | null,
  categoryTree: readonly SearchCategoryNode[],
): StorefrontChrome {
  const categorySlugs = visibleCategorySlugs([...categoryTree]);
  const headerLinks = settings?.headerLinks ?? [];
  const footerColumns = settings?.footerColumns ?? [];

  return {
    navItems: buildHeaderNavItems(headerLinks, categorySlugs),
    footerColumns: buildFooterColumns(footerColumns, categorySlugs),
    headerLogoUrl: orUndefined(settings?.headerLogoUrl ?? null),
    footerBrand: {
      wordmark: orUndefined(settings?.footerWordmark ?? null),
      tagline: orUndefined(settings?.footerTagline ?? null),
      location: orUndefined(settings?.footerLocation ?? null),
    },
    footerContact: {
      text: orUndefined(settings?.footerContact ?? null),
    },
    footerCopyrightText: resolveFooterCopyright(settings?.footerCopyright ?? null),
  };
}

export interface NavBarUserSource {
  name?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
}

/** Maps an auth user (first/last name, no `name`) to the NavBar's `user` prop; `undefined` when signed out. */
export function toNavBarUser(user: NavBarUserSource | null | undefined): NavBarUser | undefined {
  if (!user) return undefined;
  const combined = `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim();
  const name = user.name?.trim() || combined || user.email?.trim() || undefined;
  return { name };
}
